import {Injectable} from "@angular/core";
import {DeepLinkRemote} from "@app/core/shared/domain/deep-link.remote";
import {Observable} from "rxjs";
import {HandlerBuilder, HttpMethod} from "@app/core/shared/infrastructure/base.handler/base-handle.parameters";
import {catchError, map} from "rxjs/operators";
import {BaseHandleErrorMapper} from "@app/core/shared/infrastructure/base.handler/base-handle-error.mapper";
import {ProductDetails} from "@app/core/product/domain/dtos/product-details";
import {BaseHandler} from "@app/core/shared/infrastructure/base.handler/base.handler";
import {environment} from "src/environments/environment";
import {DeepLinkPath} from "@app/core/shared/infrastructure/deep-link.path";

/**
 * Implementation of a service that generates deep links using Firebase's Dynamic Links API.
 */
@Injectable()
export class DeepLinkWebService implements DeepLinkRemote{

    private readonly API_KEY = environment.FIREBASE_WEB_APY_KEY;

    private readonly DOMAIN_URI_PREFIX = "https://europastryecommerce.page.link";
    private readonly HOSTNAME = environment.HOSTNAME;
    private readonly PROTOCOL = environment.PROTOCOL;
    private readonly PORT = environment.PORT;

    private readonly ANDROID_PACKAGE_NAME = 'com.europastry.ecommerce'
    private readonly ANDROID_VERSION_NUMBER = 60;

    private readonly IOS_BUNDLE_ID = 'com.europastry.Ecommerce'

    constructor(
        private baseHandler: BaseHandler,
        ) { }

    /**
     * It creates a short link for a product, which can be used to share the product on social media
     * @param {ProductDetails} product - ProductDetails - The product details object that contains the product code, name,
     * description, and image.
     * @param {string} [title] - The title of the link. By default it is the product name.
     * @returns The short link is being returned.
     */
    createProductDeepLink(product: ProductDetails, title?: string): Observable<string> {
        const socialTitle = title ?? product.name;
        const socialDescription = product.description;
        const socialImageLink = product.image;

        const currentUrlString = window.document.URL;
        let currentUrl = new URL(currentUrlString);
        currentUrl.protocol = this.PROTOCOL
        currentUrl.hostname = this.HOSTNAME
        currentUrl.port = this.PORT
        currentUrl.pathname = `${DeepLinkPath.PRODUCT_DETAIL}/${product.code}`
        const link = currentUrl;

        const parameters = new HandlerBuilder()
            .method(HttpMethod.post)
            .url(`https://firebasedynamiclinks.googleapis.com/v1/shortLinks?key=${this.API_KEY}`)
            .body({
                dynamicLinkInfo: {
                    domainUriPrefix: this.DOMAIN_URI_PREFIX,
                    link: link,
                    androidInfo: {
                        androidPackageName: this.ANDROID_PACKAGE_NAME,
                        androidMinPackageVersionCode: this.ANDROID_VERSION_NUMBER.toString(),
                    },
                    iosInfo: {
                        iosBundleId: this.IOS_BUNDLE_ID,
                    },
                    socialMetaTagInfo: {
                        socialTitle,
                        socialDescription,
                        socialImageLink,
                    }
                }
            })
            .build()

        return this.baseHandler.execute(parameters).pipe(
            map(response => response.shortLink),
            catchError(BaseHandleErrorMapper.handleError)
        );
    }

}
